import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Container,
  Grid,
  Typography,
  Box,
  Card,
  CardContent,
  CardMedia,
  Button,
  IconButton,
  TextField,
  InputAdornment,
  Pagination,
  CircularProgress,
  Alert,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  useTheme,
  alpha
} from '@mui/material';
import { Search, Add, Remove } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { GiMedicinePills } from 'react-icons/gi';
import useUserStore from '../stores/userStore';
import useCartGuard from '../hooks/useCartGuard';

const AllMedicines = () => {
  const theme = useTheme();
  const { products, fetchProducts, plusMinus, addToCart, fetchCart } = useUserStore();
  const navigate = useNavigate();
  const { requireAuth } = useCartGuard();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [firstLetter, setFirstLetter] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [loadingStates, setLoadingStates] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [cartSyncInProgress, setCartSyncInProgress] = useState(false);

  const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

  // Memoized helper function to get the first image from the images array or string
  const getProductImage = useCallback((product) => {
    if (!product.images) {
      return null;
    }
    
    // Handle both array and string formats
    let firstImage;
    if (Array.isArray(product.images)) {
      // If images is an array, get the first URL
      firstImage = product.images[0];
    } else {
      // If images is a string, split and get the first image name
      const imageNames = product.images.split(',');
      firstImage = imageNames[0].trim();
      // Construct the path to the Drugs Images folder in public assets
      firstImage = `/assets/Drugs Images/${firstImage}`;
    }
    
    return firstImage || null;
  }, []);

  // Function to handle product card click and navigate to product detail page
  const handleProductClick = useCallback((productId) => {
    navigate(`/product/${productId}`);
  }, [navigate]);

  const ProductCard = React.memo(({ product, index }) => (
    <Card 
      onClick={() => handleProductClick(product.id)}
      sx={{ 
        height: { xs: '180px', sm: '190px', md: '180px' ,lg: '190px', xl: '200px'},
        width: { xs: '320px', sm: '300px', md: '320px', lg: '360px', xl: '360px' },
        display: 'flex', 
        flexDirection: 'column',
        borderRadius: '12px',
        boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
        transition: 'all 0.25s ease',
        border: '1px solid',
        borderColor: 'divider',
        backgroundColor: '#ffffff',
        cursor: 'pointer',
     
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 10px 24px rgba(0, 0, 0, 0.12)',
          borderColor: 'primary.main'
        }
      }}>
      {/* Main Content Row */}
      <Box sx={{ 
        display: 'flex',
        alignItems: 'flex-start',
        gap: { xs: '8px', sm: '10px', md: '12px' },
        padding: { xs: '10px', sm: '11px', md: '12px' },
        flex: 1,
        minHeight: { xs: '100px', sm: '110px', md: '120px' }
      }}>
        {/* Left Side: Product Image */}
        <Box sx={{ 
          flexShrink: 0,
          width: { xs: '50px', sm: '55px', md: '60px' },
          height: { xs: '60px', sm: '65px', md: '70px' },
          backgroundColor: alpha(theme.palette.primary.main, 0.08),
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden'
        }}>
          {getProductImage(product) ? (
            <CardMedia
              component="img"
              image={getProductImage(product)}
             
              sx={{ 
                objectFit: 'contain',
                maxWidth: '100%',
                maxHeight: '100%',
                transition: 'all 0.3s ease'
              }}
            />
          ) : (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                height: '100%'
              }}
            >
              <GiMedicinePills 
                size={40} 
                color={theme.palette.primary.main} 
              />
            </Box>
          )}
        </Box>

        {/* Right Side: Product Info */}
        <Box sx={{ 
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
          height: '100%',
          justifyContent: 'space-between'
        }}>
          {/* Product Name */}
          <Typography variant="h6" component="div" sx={{ 
            fontSize: { xs: '0.9rem', sm: '0.95rem', md: '1rem' },
            fontWeight: 600,
            color: 'text.primary',
            lineHeight: 1.2,
            display: '-webkit-box',
            WebkitLineClamp: { xs: 1, sm: 1, md: 1 },
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            mb: { xs: '3px', sm: '3px', md: '4px' }
          }}>
            {product.name}
          </Typography>
          
          {/* Prescription Required Badge */}
          {product.prescription_required && (
            <Box sx={{ mb: { xs: '3px', sm: '3px', md: '4px' } }}>
              <Chip 
                icon={
                  <Box sx={{ 
                    width: { xs: '8px', sm: '9px', md: '10px' }, 
                    height: { xs: '8px', sm: '9px', md: '10px' }, 
                    borderRadius: '50%', 
                    backgroundColor: '#ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: { xs: '6px', sm: '6px', md: '7px' },
                    color: 'primary.contrastText',
                    fontWeight: 'bold'
                  }}>R</Box>
                }
                label="Prescription Required" 
                size="small" 
                sx={{ 
                  backgroundColor: 'secondary.light',
                  color: '#ffffff',
                  fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.7rem' },
                  height: { xs: '16px', sm: '17px', md: '18px' },
                  borderRadius: { xs: '8px', sm: '8px', md: '9px' }
                }} 
              />
            </Box>
          )}
          
          {/* Product Details */}
          <Box sx={{ 
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            minHeight: { xs: '40px', sm: '45px', md: '50px' }
          }}>
            {/* Packaging Info */}
            <Typography variant="body2" sx={{ 
              fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.8rem' },
              color: 'text.secondary',
              fontWeight: 400,
              mb: { xs: '2px', sm: '3px', md: '3px' },
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              minHeight: { xs: '12px', sm: '13px', md: '14px' }
            }}>
              {product.packaging || 'strip of 10 Tablets'}
            </Typography>
            
            {/* Manufacturer */}
            <Typography variant="body2" sx={{ 
              fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.8rem' },
              color: 'text.secondary',
              fontWeight: 400,
              mb: { xs: '2px', sm: '3px', md: '3px' },
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              minHeight: { xs: '12px', sm: '13px', md: '14px' }
            }}>
              By {product.manufacturer}
            </Typography>
            
            {/* Salt Composition */}
            {product.salt_composition && (
              <Typography variant="body2" sx={{ 
                fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.8rem' },
                color: 'text.secondary',
                fontWeight: 400,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                minHeight: { xs: '12px', sm: '13px', md: '14px' }
              }}>
                {product.salt_composition}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      {/* Bottom Row: Price and Action */}
      <Box sx={{ 
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: { xs: '10px', sm: '11px', md: '12px' },
        borderTop: '1px solid',
        borderColor: 'divider',
        backgroundColor: 'background.default',
        minHeight: { xs: '40px', sm: '45px', md: '50px' },
        flexShrink: 0
      }}>
        {/* Left Side: Price with Discount */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {product.best_price && product.best_price !== product.price ? (
            <>
              {/* Original MRP with strikethrough */}
              <Typography variant="body2" sx={{ 
                fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.85rem' },
                color: 'text.secondary',
                textDecoration: 'line-through',
                lineHeight: 1.2
              }}>
                MRP ₹{parseFloat(product.price).toFixed(2)}
              </Typography>
              {/* Discounted Price */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h6" sx={{ 
                  fontSize: { xs: '0.9rem', sm: '0.95rem', md: '1rem' },
                  fontWeight: 600,
                  color: 'text.primary',
                  lineHeight: 1.2
                }}>
                  ₹{parseFloat(product.best_price).toFixed(2)}
                </Typography>
                {product.discount_percent && (
                  <Chip
                    label={`${parseFloat(product.discount_percent).toFixed(0)}% OFF`}
                    size="small"
                    sx={{
                      backgroundColor: 'success.main',
                      color: 'white',
                      fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.7rem' },
                      height: { xs: '18px', sm: '20px', md: '20px' },
                      fontWeight: 600,
                      '& .MuiChip-label': {
                        px: { xs: '6px', sm: '8px', md: '8px' }
                      }
                    }}
                  />
                )}
              </Box>
            </>
          ) : (
            <Typography variant="h6" sx={{ 
              fontSize: { xs: '0.9rem', sm: '0.95rem', md: '1rem' },
              fontWeight: 600,
              color: 'text.primary',
              lineHeight: 1.2
            }}>
              MRP ₹{parseFloat(product.price).toFixed(2)}
            </Typography>
          )}
        </Box>

        {/* Right Side: Action Button */}
        <Box>
          {product.stock_status === 'out_of_stock' ? (
            <Typography variant="body2" sx={{ 
              fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.7rem' },
              fontWeight: 500,
              color: 'text.disabled'
            }}>
              NOT AVAILABLE
            </Typography>
          ) : !product.cart || product.cart <= 0 ? (
            <Button
              variant="contained"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleAddToCart(product.id);
              }}
              disabled={loadingStates[product.id]}
              sx={{ 
                backgroundColor: 'primary.main',
                color: '#ffffff',
                fontWeight: 600,
                textTransform: 'none',
                fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.85rem' },
                px: { xs: '14px', sm: '16px', md: '18px' },
                borderRadius: '20px',
                boxShadow: 'none',
                height: { xs: '30px', sm: '32px', md: '34px' },
                minWidth: { xs: '64px', sm: '70px', md: '76px' },
                '&:hover': {
                  backgroundColor: 'primary.dark',
                  boxShadow: '0 4px 12px rgba(83, 126, 188, 0.3)'
                }
              }}
            >
              {loadingStates[product.id] ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                'Add'
              )}
            </Button>
          ) : (
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              border: '1px solid',
              borderColor: 'primary.main',
              borderRadius: '20px',
              overflow: 'hidden',
              height: { xs: '30px', sm: '32px', md: '34px' },
              backgroundColor: '#ffffff'
            }}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleQuantityChange(product.id, 'dec', index);
                }}
                disabled={!product.cart || product.cart <= 0}
                sx={{
                  borderRadius: 0,
                  width: { xs: '28px', sm: '30px', md: '32px' },
                  height: '100%',
                  color: 'primary.main',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.08)
                  },
                  '&.Mui-disabled': {
                    color: alpha(theme.palette.primary.main, 0.3)
                  }
                }}
              >
                <Remove fontSize="small" />
              </IconButton>
              <Typography sx={{
                px: 1,
                minWidth: { xs: '22px', sm: '24px', md: '26px' },
                textAlign: 'center',
                fontWeight: 700,
                fontSize: { xs: '0.85rem', sm: '0.9rem', md: '0.95rem' },
                color: 'text.primary'
              }}>
                {product.cart || 0}
              </Typography>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleQuantityChange(product.id, 'inc', index);
                }}
                sx={{
                  borderRadius: 0,
                  width: { xs: '28px', sm: '30px', md: '32px' },
                  height: '100%',
                  color: 'primary.main',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.08)
                  }
                }}
              >
                <Add fontSize="small" />
              </IconButton>
            </Box>
          )}
        </Box>
      </Box>
    </Card>
  ), (prevProps, nextProps) => {
    // Only re-render if the product's cart quantity or essential properties change
    return (
      prevProps.product.id === nextProps.product.id &&
      prevProps.product.cart === nextProps.product.cart &&
      prevProps.product.stock_status === nextProps.product.stock_status &&
      prevProps.product.name === nextProps.product.name &&
      prevProps.product.price === nextProps.product.price &&
      prevProps.product.images === nextProps.product.images
    );
  });

  // Memoize the products grid to prevent unnecessary re-renders
  const memoizedProductsGrid = useMemo(() => {
    if (products.length === 0) return null;
    
    return products.map((product, index) => (
      <Grid 
        item 
        xs={12}    // Mobile: 1 column (full width)
        sm={6}     // Tablet: 2 columns
        md={4}     // Desktop: 3 columns
        lg={4}     // Large Desktop: 3 columns
        xl={3}     // Extra Large: 3 columns
        key={product.id}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent:'space-between',
          minHeight: { xs: '160px', sm: '170px', md: '200px' },
          maxWidth: { xs: '100%', sm: '100%', md: '350px' }
        }}
      >
        <ProductCard product={product} index={index} />
      </Grid>
    ));
  }, [products]);

  useEffect(() => {
    loadProducts();
  }, [currentPage, firstLetter, sortBy]);

  // Sync cart data when component mounts
  useEffect(() => {
    const initializeCartSync = async () => {
      try {
        await syncCartWithProducts();
      } catch (error) {
        console.error('Error initializing cart sync:', error);
      }
    };
    
    initializeCartSync();
  }, []);

  // Sync cart data with products
  const syncCartWithProducts = async () => {
    if (cartSyncInProgress) {
      console.log('Cart sync already in progress, skipping...');
      return;
    }
    
    try {
      setCartSyncInProgress(true);
      console.log('Starting cart sync...');
      
      const cartData = await fetchCart();
      if (cartData && cartData.cart && cartData.cart.cart_items && cartData.cart.cart_items.length > 0) {
        const cartItems = cartData.cart.cart_items;
        
        // Create a map of product IDs to cart quantities
        const cartMap = cartItems.reduce((acc, cartItem) => {
          if (cartItem.product) {
            acc[cartItem.product.id] = cartItem.quantity;
          }
          return acc;
        }, {});
        
        // Update products with cart quantities
        const updatedProducts = products.map(product => ({
          ...product,
          cart: cartMap[product.id] || 0
        }));
        
        useUserStore.getState().setProducts(updatedProducts);
        console.log('Cart sync completed successfully');
      }
    } catch (error) {
      console.error('Error syncing cart with products:', error);
    } finally {
      setCartSyncInProgress(false);
    }
  };

  // Initialize cart property for products when component mounts
  useEffect(() => {
    if (products.length > 0) {
      // Only sync if products don't have cart property initialized
      const needsCartSync = products.some(product => product.cart === undefined);
      if (needsCartSync) {
        syncCartWithProducts();
      }
    }
  }, [products]);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const params = {
        first_letter: firstLetter,
        page: currentPage,
      };
      
      const result = await fetchProducts(params);
      setTotalPages(result.num_pages || 1);
      
      // Cart sync will be handled by the useEffect when products change
    } catch (err) {
      console.error('Error loading products:', err);
      setError('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (searchTerm.trim()) {
      try {
        setLoading(true);
        const result = await fetchProducts({
          search: searchTerm,
          page: 1,
        });
        setTotalPages(result.num_pages || 1);
        setCurrentPage(1);
        
        // Cart sync will be handled by the useEffect when products change
      } catch (err) {
        console.error('Error searching products:', err);
        setError('Failed to search products');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleAddToCart = useCallback(async (productId) => {
    if (!requireAuth()) return;
    try {
      setLoadingStates(prev => ({ ...prev, [productId]: true }));
      await addToCart(productId);
      
      // Update only the specific product in the store without triggering full re-render
      const currentProducts = useUserStore.getState().products;
      const updatedProducts = currentProducts.map(product => 
        product.id === productId 
          ? { ...product, cart: (product.cart || 0) + 1 }
          : product
      );
      useUserStore.getState().setProducts(updatedProducts);
      
      setSnackbar({ open: true, message: 'Item added to cart successfully!', severity: 'success' });
    } catch (err) {
      console.error('Error adding to cart:', err);
      setSnackbar({ open: true, message: 'Failed to add item to cart', severity: 'error' });
    } finally {
      setLoadingStates(prev => ({ ...prev, [productId]: false }));
    }
  }, [addToCart, requireAuth]);

  const handleQuantityChange = useCallback(async (productId, action, index) => {
    if (!requireAuth()) return;
    try {
      await plusMinus({
        productId,
        action,
        source: 'product',
        index,
        productType: 'product', // AllMedicines deals with regular products
      });
    } catch (error) {
      console.error('Error changing quantity:', error);
      setSnackbar({ open: true, message: 'Failed to update cart', severity: 'error' });
    }
  }, [plusMinus, requireAuth]);


  return (
    <>
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        {/* Header Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h1" sx={{ 
            fontWeight: 700, 
            color: 'text.primary',
            mb: 3,
            fontSize: '2rem'
          }}>
            Medicine Index {firstLetter && `starting with - ${firstLetter}`}
          </Typography>
          
          {/* Alphabet Filter Bar */}
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
            <Box sx={{ 
              display: 'grid',
              gridTemplateColumns: 'repeat(13, 1fr)',
              gap: 0.5,
              maxWidth: 'fit-content',
              width: '100%',
              '@media (max-width: 600px)': {
                gridTemplateColumns: 'repeat(13, 1fr)',
                gap: 0.25
              }
            }}>
              {letters.map((letter) => (
                <Button
                  key={letter}
                  variant="outlined"
                  size="small"
                  onClick={() => setFirstLetter(firstLetter === letter ? '' : letter)}
                  sx={{
                    minWidth: '32px',
                    width: '100%',
                    height: '32px',
                    borderRadius: '4px',
                    fontSize: '14px',
                    fontWeight: 500,
                    textTransform: 'none',
                    backgroundColor: firstLetter === letter ? 'primary.main' : 'transparent',
                    color: firstLetter === letter ? 'white' : 'text.primary',
                    borderColor: firstLetter === letter ? 'primary.main' : 'divider',
                    '&:hover': {
                      backgroundColor: firstLetter === letter ? 'primary.dark' : 'secondary.main',
                      borderColor: firstLetter === letter ? 'primary.dark' : 'divider',
                      color: 'white'
                    }
                  }}
                >
                  {letter}
                </Button>
              ))}
            </Box>
          </Box>

          {/* Results Count */}
          <Typography variant="body2" sx={{ 
            color: 'text.secondary',
            fontSize: '0.875rem',
            mb: 2
          }}>
            Showing 1-{products.length} of {products.length} results
          </Typography>
        </Box>

        {/* Search and Filters */}
        {/* <Box sx={{ mb: 4 }}>
          <Grid container spacing={{ xs: 2, sm: 2, md: 2 }} alignItems="center">
            <Grid item xs={12} sm={12} md={6} lg={6}>
              <TextField
                fullWidth
                placeholder="Search medicines..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: { xs: 2, sm: 0 } }}
              />
            </Grid>
            <Grid item xs={6} sm={6} md={3} lg={3}>
              <FormControl fullWidth>
                <InputLabel>First Letter</InputLabel>
                <Select
                  value={firstLetter}
                  label="First Letter"
                  onChange={(e) => setFirstLetter(e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  {letters.map((letter) => (
                    <MenuItem key={letter} value={letter}>
                      {letter}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={6} md={3} lg={3}>
              <FormControl fullWidth>
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={sortBy}
                  label="Sort By"
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <MenuItem value="name">Name</MenuItem>
                  <MenuItem value="price">Price</MenuItem>
                  <MenuItem value="manufacturer">Manufacturer</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box> */}

        {/* Products Grid */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : products.length > 0 ? (
          <>
            <Grid 
              container 
              spacing={{ xs: 2, sm: 2, md: 3, lg: 3, xl: 3 }}
              sx={{ 
                alignItems: 'stretch',
                justifyContent: 'center'
              }}
            >
              {memoizedProductsGrid}
            </Grid>

            {/* Pagination */}
            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={(event, page) => {
                    setCurrentPage(page);
                    window.scrollTo({ top: 0, behavior: 'instant' });
                  }}
                  color="primary"
                  size="large"
                />
              </Box>
            )}
          </>
        ) : (
          <Alert severity="info">
            No products found. Try adjusting your search criteria.
          </Alert>
        )}
      </Container>
      
      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AllMedicines;

